import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Blokk, Cim, Tabla, UresSor } from '../elemzes/Blokk.tsx';
import IdoszakValaszto from '../elemzes/IdoszakValaszto.tsx';
import { idoszakMondat, idoszakSzamitas, type IdoszakTipus } from '../elemzes/idoszak.ts';
import { aranySzoveg } from '../elemzes/valtozas.ts';
import { ALLAPOT_CIMKE, ALLAPOT_SORREND, allapotCimke, ELTERES_CIMKE, elteresCimke, elteresSulyossag, kevesValasz, legsulyosabb, rangCimke, rendezodott, SULYOSSAG_CIMKE, valaszCimke, type Sulyossag } from '../minoseg/cimkek.ts';
import { ellenorizMinoseg, MinosegAlakHiba, type MinosegElteres, type MinosegValasz } from '../minoseg/tipusok.ts';
import { formatDatum } from '../lib/formaz.ts';
import { supabase } from '../lib/supabase.ts';
import { Betolto, Gomb, HibaUzenet, Mezo, OldalFej, Parbeszed, Ures, Valaszto } from '../ui/elemek.tsx';

async function minosegLekeres(tol: string, ig: string): Promise<MinosegValasz> {
  const { data, error } = await supabase.rpc('minoseg_osszesito', { p_tol: tol, p_ig: ig });
  if (error) throw error;
  return ellenorizMinoseg(data);
}

function Hiba({ hiba }: { hiba: unknown }) {
  // Az alakhiba üzenete már érthető magyar szöveg; az egyéb hibákat a közös hibaszöveg fordítja.
  if (hiba instanceof MinosegAlakHiba) return <p role="alert" className="rounded-xl border border-hiba bg-hiba-hatter px-3.5 py-2.5 text-sm text-hiba">{hiba.message}</p>;
  return <HibaUzenet hiba={hiba} />;
}

const SULYOSSAG_SZIN: Record<Sulyossag, string> = { sulyos: 'text-hiba', kozepes: 'text-masodlagos', tajekoztato: 'text-szoveg-halk' };

/** „Valaszolt/kiküldött” arány a látogató és a CRM szerinti számmal egymás mellett. */
function Par({ latogato, crm }: { latogato: number; crm: number }) {
  return <>{latogato} <span className="text-szoveg-halk">/ {crm}</span></>;
}

export default function SuperMinoseg() {
  const [parameterek, setParameterek] = useSearchParams();
  const leadSzuro = parameterek.get('lead');
  // A riasztó levél linkje (?lead=…) régebbi, még 60 napos ablakban lévő kiküldésre is mutathat: ilyenkor 120 napot nézünk.
  const [tipus, setTipus] = useState<IdoszakTipus>(leadSzuro ? 'egyedi' : '30nap');
  const [egyedi, setEgyedi] = useState(() => {
    const most = new Date();
    if (leadSzuro) {
      const kezdet = idoszakSzamitas('30nap', new Date(most.getTime() - 90 * 86_400_000));
      return { tol: kezdet.tolNap, ig: idoszakSzamitas('30nap', most).utolsoNap };
    }
    const i = idoszakSzamitas('30nap', most);
    return { tol: i.tolNap, ig: i.utolsoNap };
  });
  const [tipusSzuro, setTipusSzuro] = useState('');
  const [allapotSzuro, setAllapotSzuro] = useState('');
  const [irodaSzuro, setIrodaSzuro] = useState('');
  const [kezelt, setKezelt] = useState<MinosegElteres | null>(null);

  const idoszak = useMemo(() => {
    try { return { ertek: idoszakSzamitas(tipus, new Date(), egyedi), hiba: null }; }
    catch (e) { return { ertek: null, hiba: e instanceof Error ? e.message : 'Érvénytelen időszak.' }; }
  }, [tipus, egyedi]);

  const lekeres = useQuery({
    queryKey: ['minoseg', idoszak.ertek?.tol, idoszak.ertek?.ig],
    enabled: idoszak.ertek !== null,
    queryFn: () => minosegLekeres(idoszak.ertek!.tol, idoszak.ertek!.ig),
  });
  const d = lekeres.data;

  const lista = useMemo(() => (d?.elteresek ?? []).filter((e) =>
    (!leadSzuro || e.lead_id === leadSzuro)
    && (tipusSzuro === '' || e.elteresek.includes(tipusSzuro))
    && (allapotSzuro === '' || e.elteres_allapot === allapotSzuro)
    && (irodaSzuro === '' || e.office_id === irodaSzuro)), [d, leadSzuro, tipusSzuro, allapotSzuro, irodaSzuro]);

  const leiras = idoszak.ertek ? `${idoszakMondat(idoszak.ertek.tolNap, idoszak.ertek.utolsoNap)} kiküldött kérdőívek: a látogatók válasza a CRM-státusszal összevetve.` : undefined;

  return (
    <>
      <OldalFej cim="Minőség" leiras={leiras}>
        <IdoszakValaszto ertek={tipus} onValt={setTipus} />
      </OldalFej>

      {tipus === 'egyedi' && (
        <div className="mb-5 grid max-w-md grid-cols-2 gap-3">
          <Mezo label="Kezdő nap" type="date" value={egyedi.tol} onChange={(e) => setEgyedi((x) => ({ ...x, tol: e.target.value }))} />
          <Mezo label="Záró nap" type="date" value={egyedi.ig} onChange={(e) => setEgyedi((x) => ({ ...x, ig: e.target.value }))} />
        </div>
      )}
      {idoszak.hiba && <p role="alert" className="mb-5 rounded-xl border border-hiba bg-hiba-hatter px-3.5 py-2.5 text-sm text-hiba">{idoszak.hiba}</p>}
      <Hiba hiba={lekeres.error} />
      {lekeres.isPending && idoszak.ertek && <Betolto />}

      {d && d.irodak.length === 0 && d.elteresek.length === 0 && (
        <Ures cim="Ebben az időszakban nem ment ki kérdőív." szoveg="A kérdőív a megerősítés után 90–120 nappal megy ki, és csak az ÁSZF 1.1-et elfogadó iroda leadjeinek. Válassz hosszabb időszakot, vagy nézz vissza később." />
      )}
      {d && (d.irodak.length > 0 || d.elteresek.length > 0) && (
        <div className="grid gap-[18px]">
          <Blokk cim="Eltérések" leiras="Ahol a látogató válasza és a CRM-státusz nem fér össze. A válasz önmagában nem állapít meg szerződésszegést: egyeztetés indul, a lezárást itt rögzítjük.">
            <div role="group" aria-label="Szűrők" className="mt-4 grid gap-3 sm:grid-cols-3">
              <Valaszto label="Típus" value={tipusSzuro} onChange={(e) => setTipusSzuro(e.target.value)}>
                <option value="">Mind</option>
                {Object.entries(ELTERES_CIMKE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Valaszto>
              <Valaszto label="Állapot" value={allapotSzuro} onChange={(e) => setAllapotSzuro(e.target.value)}>
                <option value="">Mind</option>
                {ALLAPOT_SORREND.map((k) => <option key={k} value={k}>{ALLAPOT_CIMKE[k]}</option>)}
              </Valaszto>
              <Valaszto label="Iroda" value={irodaSzuro} onChange={(e) => setIrodaSzuro(e.target.value)}>
                <option value="">Mind</option>
                {d.irodak.map((i) => <option key={i.office_id} value={i.office_id}>{i.nev}</option>)}
              </Valaszto>
            </div>
            {leadSzuro && (
              <p className="mt-3 flex flex-wrap items-center gap-3 text-sm text-szoveg-halk">
                Egyetlen lead eltérése látszik.
                <Gomb valtozat="masodlagos" onClick={() => setParameterek({}, { replace: true })}>Szűrés megszüntetése</Gomb>
              </p>
            )}
            {lista.length > 0 ? (
              <ul aria-label="Eltérések listája" className="mt-4 grid gap-3">
                {lista.map((e) => <ElteresKartya key={e.id} e={e} onKezel={() => setKezelt(e)} />)}
              </ul>
            ) : <UresSor szoveg={d.elteresek.length === 0 ? 'Ebben az időszakban nincs eltérés.' : 'A szűrésnek egy eltérés sem felel meg.'} />}
          </Blokk>

          <Blokk cim="Irodák" leiras="Irodánként számolunk, értékesítőnként nem. A párok: a látogató szerinti szám / a CRM szerinti szám a megválaszolt kérdőívekre. Az idő a kiküldés napja szerint értendő.">
            {d.irodak.length > 0 ? (
              <Tabla nev="Irodák minősége" minSzel={760} fej={[
                { cim: 'Iroda' }, { cim: 'Kiküldve', j: true }, { cim: 'Válaszolt', j: true }, { cim: 'Kapcsolat (igen)', j: true },
                { cim: 'Szemle (látogató / CRM)', j: true }, { cim: 'Megbízás (látogató / CRM)', j: true }, { cim: 'Nyitott eltérés', j: true }, { cim: 'Elégedettség', j: true },
              ]}>
                {d.irodak.map((i) => (
                  <tr key={i.office_id}>
                    <Cim nev={i.nev} alcim={kevesValasz(i.valaszolt) ? 'kevés válasz, az arányok tájékoztató jellegűek' : undefined} />
                    <td className="j szam">{i.kikuldve}</td>
                    <td className="j szam">{i.valaszolt} <span className="text-szoveg-halk">({aranySzoveg(i.valaszarany)})</span></td>
                    <td className="j szam">{aranySzoveg(i.kapcsolat_igen_arany)}</td>
                    <td className="j szam"><Par latogato={i.szemle_latogato} crm={i.szemle_crm} /></td>
                    <td className="j szam"><Par latogato={i.megbizas_latogato} crm={i.megbizas_crm} /></td>
                    <td className="j szam">{i.elteres_nyitott}{i.megbizas_rejtett > 0 && <span className="text-hiba"> (rejtett megbízás: {i.megbizas_rejtett})</span>}</td>
                    <td className="j szam">{i.elegedettseg_atlag === null ? '–' : `${i.elegedettseg_atlag.toLocaleString('hu-HU')} / 5`}</td>
                  </tr>
                ))}
              </Tabla>
            ) : <UresSor szoveg="Ebben az időszakban egyik irodának sem ment ki kérdőív." />}
          </Blokk>
        </div>
      )}
      <div className="mt-[18px]"><TiltakozasRogzites /></div>
      <LezarasParbeszed key={kezelt?.id ?? 'nincs'} e={kezelt} onBezar={() => setKezelt(null)} />
    </>
  );
}

/** E-mailben vagy más úton érkezett tiltakozás: a cím hash-e a tiltólistára kerül, a címet nem tároljuk. */
function TiltakozasRogzites() {
  const [email, setEmail] = useState('');
  const [kesz, setKesz] = useState<string | null>(null);
  const mentes = useMutation({
    mutationFn: async (cim: string) => {
      const { error } = await supabase.rpc('minoseg_tiltolistara', { p_email: cim });
      if (error) throw new Error(error.code === '22023' ? 'Érvénytelen e-mail-cím.' : 'A rögzítés nem sikerült. Próbáld újra.');
    },
    onSuccess: (_v, cim) => { setKesz(cim); setEmail(''); },
  });
  return (
    <Blokk cim="Tiltakozás rögzítése" leiras="Ha valaki e-mailben vagy telefonon jelzi, hogy nem kér minőségellenőrző kérdést, itt rögzítsd. A cím nem kap több kérdőívet, a már sorban álló levél sem megy ki.">
      <form className="mt-4 flex max-w-xl flex-wrap items-end gap-3" onSubmit={(ev) => { ev.preventDefault(); setKesz(null); mentes.mutate(email.trim()); }}>
        <Mezo label="E-mail-cím" type="email" required className="min-w-[16rem] flex-1" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Gomb type="submit" toltes={mentes.isPending}>Tiltólistára teszem</Gomb>
      </form>
      {mentes.error && <p role="alert" className="mt-3 text-sm text-hiba">{mentes.error.message}</p>}
      {kesz && <p role="status" className="mt-3 text-sm text-szoveg-halk">Rögzítve: {kesz} nem kap több kérdőívet.</p>}
    </Blokk>
  );
}

function ElteresKartya({ e, onKezel }: { e: MinosegElteres; onKezel(): void }) {
  const sulyossag = legsulyosabb(e.elteresek);
  const rendezett = rendezodott(e.elteresek, e.crm_elert_rang_most);
  return (
    <li data-testid="elteres" className="grid gap-3 rounded-2xl border border-vonal bg-hatter-melyen p-4 sm:p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="text-[15px] font-medium">{e.iroda_nev}</h3>
        <p className="text-xs text-szoveg-halk">
          Kiküldve: <span className="szam">{formatDatum(e.kuldve_at)}</span>
          {e.valaszolt_at && <> · Válasz: <span className="szam">{formatDatum(e.valaszolt_at)}</span></>}
        </p>
      </div>
      <ul aria-label="Eltérés-típusok" className="grid gap-1 text-sm">
        {e.elteresek.map((t) => (
          <li key={t} className={SULYOSSAG_SZIN[elteresSulyossag(t)]}>
            <span className="font-medium">{SULYOSSAG_CIMKE[elteresSulyossag(t)]}:</span> {elteresCimke(t)}
          </li>
        ))}
      </ul>
      <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <div><dt className="inline text-szoveg-halk">Kiküldéskor a CRM-ben: </dt><dd className="inline">{rangCimke(e.crm_elert_rang_kuldeskor)}</dd></div>
        <div>
          <dt className="inline text-szoveg-halk">Most a CRM-ben: </dt>
          <dd className="inline">{rangCimke(e.crm_elert_rang_most)}{rendezett && <span className="text-kiemelt"> · azóta rendeződött</span>}</dd>
        </div>
        <div><dt className="inline text-szoveg-halk">Kapcsolatfelvétel: </dt><dd className="inline">{valaszCimke('kapcsolat', e.valaszok.kapcsolat)}</dd></div>
        <div><dt className="inline text-szoveg-halk">Szemle: </dt><dd className="inline">{valaszCimke('szemle', e.valaszok.szemle)}</dd></div>
        <div><dt className="inline text-szoveg-halk">Megbízás: </dt><dd className="inline">{valaszCimke('megbizas', e.valaszok.megbizas)}</dd></div>
        <div><dt className="inline text-szoveg-halk">Elégedettség: </dt><dd className="inline">{e.valaszok.elegedettseg === null ? '–' : `${e.valaszok.elegedettseg} / 5`}</dd></div>
      </dl>
      {e.valaszok.megjegyzes && <blockquote className="wrap-anywhere whitespace-pre-wrap border-l-2 border-vonal-eros pl-3 text-sm">{e.valaszok.megjegyzes}</blockquote>}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-vonal pt-3">
        <p className="min-w-0 flex-1 text-sm">
          <span className="text-szoveg-halk">Állapot: </span><b className="font-medium">{allapotCimke(e.elteres_allapot)}</b>
          {e.elteres_kezelve_at && <span className="text-szoveg-halk"> ({formatDatum(e.elteres_kezelve_at)})</span>}
          {e.elteres_megjegyzes && <span className="wrap-anywhere block text-szoveg-halk">{e.elteres_megjegyzes}</span>}
        </p>
        <Link to={`/leadek/${e.lead_id}`} className="inline-flex min-h-10 items-center text-sm text-kiemelt underline">Lead megnyitása</Link>
        <Gomb valtozat="masodlagos" onClick={onKezel}>{e.elteres_allapot === 'nyitott' || e.elteres_allapot === null ? 'Lezárás' : 'Állapot módosítása'}</Gomb>
      </div>
    </li>
  );
}

function LezarasParbeszed({ e, onBezar }: { e: MinosegElteres | null; onBezar(): void }) {
  const kliens = useQueryClient();
  const [allapot, setAllapot] = useState(e && e.elteres_allapot && e.elteres_allapot !== 'nyitott' ? e.elteres_allapot : 'rendben');
  const [megjegyzes, setMegjegyzes] = useState(e?.elteres_megjegyzes ?? '');
  const mentes = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('minoseg_elteres_kezeles', { p_id: e!.id, p_allapot: allapot, p_megjegyzes: megjegyzes });
      if (error) throw error;
    },
    onSuccess: async () => {
      await kliens.invalidateQueries({ queryKey: ['minoseg'] });
      await kliens.invalidateQueries({ queryKey: ['minoseg-panel'] });
      onBezar();
    },
  });
  return (
    <Parbeszed nyitva={e !== null} cim="Eltérés lezárása" onBezar={onBezar}>
      <form className="grid gap-4" onSubmit={(ev) => { ev.preventDefault(); mentes.mutate(); }}>
        <p className="text-sm text-szoveg-halk">A lezárás csak nyilvántartás: a CRM-et nem módosítja, és nem von maga után szankciót.</p>
        <Valaszto label="Állapot" value={allapot} onChange={(ev) => setAllapot(ev.target.value)}>
          {ALLAPOT_SORREND.map((k) => <option key={k} value={k}>{ALLAPOT_CIMKE[k]}</option>)}
        </Valaszto>
        <Mezo label="Jegyzet" multiline maxLength={2000} value={megjegyzes} onChange={(ev) => setMegjegyzes(ev.target.value)} suly="Az egyeztetés eredménye, az iroda nyilatkozata (legfeljebb 2000 karakter)." />
        <HibaUzenet hiba={mentes.error} />
        <div className="flex flex-wrap justify-end gap-3">
          <Gomb valtozat="masodlagos" onClick={onBezar}>Mégsem</Gomb>
          <Gomb type="submit" toltes={mentes.isPending}>Mentés</Gomb>
        </div>
      </form>
    </Parbeszed>
  );
}
